import { startOfDay } from 'date-fns';

/**
 * Commercial (`saleDate`) vs system registration (`createdAt`).
 * Backend stores both; UI should show the commercial date for "fecha de venta"
 * and optionally registration when they differ (e.g. ventas cargadas días después).
 */
export type CommercialSaleDateSource = {
  saleDate?: string | Date | null;
  createdAt: string | Date;
};

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Resolved commercial date; falls back to `createdAt` if `saleDate` is missing or invalid. */
export function getCommercialSaleDate(order: CommercialSaleDateSource): Date {
  if (order.saleDate != null && order.saleDate !== '') {
    const d = toDate(order.saleDate);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }
  }
  const fallback = toDate(order.createdAt);
  return Number.isNaN(fallback.getTime()) ? new Date() : fallback;
}

/** True when calendar day of sale differs from registration (backdated / late entry). */
export function commercialSaleDateDiffersFromRegistration(
  order: CommercialSaleDateSource,
): boolean {
  const regDate = toDate(order.createdAt);
  if (Number.isNaN(regDate.getTime())) return false;
  const saleDay = startOfDay(getCommercialSaleDate(order)).getTime();
  const regDay = startOfDay(regDate).getTime();
  return saleDay !== regDay;
}

/** Fecha de venta comercial: solo día (sin hora) para tablas e informes. */
export function formatCommercialSaleDate(
  order: CommercialSaleDateSource,
  locale: string = 'es-ES',
  options?: { includeTime?: boolean },
): string {
  const date = getCommercialSaleDate(order);
  if (options?.includeTime === true) {
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Momento de registro en sistema (fecha y hora). */
export function formatRegistrationDateTime(
  createdAt: string | Date,
  locale: string = 'es-ES',
): string {
  const d = toDate(createdAt);
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
